import { authFetchInit, getAuthHeaders, hasAuthSession } from '../utils/authSession';
import React from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { Cancel as CancelIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { API_ROOT } from '../config/apiConfig';
import { useTheme } from '../contexts/ThemeContext';
import { chipSxFromSurface, getRiskSurface } from '../utils/appearanceTokens';

const SSO_STEPS = [
  { label: 'Submitted', sub: 'Config submitted for review' },
  { label: 'Under Review', sub: 'SSO admin reviewing' },
  { label: 'Approved', sub: 'Configuration approved' },
  { label: 'Onboarded to IdP', sub: 'App created in IdP' },
];

const getStepIndex = (status: string): number => {
  switch (status) {
    case 'Pending Review':
      return 0;
    case 'In Progress':
    case 'Need More Info':
    case 'Rejected':
      return 1;
    case 'Approved':
      return 2;
    case 'Completed':
      return 3;
    default:
      return 0;
  }
};

export const SSOProgressStepper: React.FC<{ status: string }> = ({ status }) => {
  const { resolvedMode } = useTheme();
  const isDark = resolvedMode === 'dark';
  const activeStep = getStepIndex(status);
  const isRejected = status === 'Rejected';
  const muted = isDark ? '#9aa0a6' : '#9ca3af';
  const inactiveBorder = isDark ? '#4a5568' : '#d1d5db';
  const inactiveTrack = isDark ? '#2d3748' : '#e5e7eb';
  const activeText = isDark ? '#e8eaed' : '#1f2937';
  const ringOk = isDark ? 'rgba(99, 102, 241, 0.28)' : '#e0e7ff';
  const ringBad = isDark ? 'rgba(239, 68, 68, 0.28)' : '#fee2e2';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mb: 2.5, mt: 0.5 }}>
      {SSO_STEPS.map((step, idx) => {
        const done = idx < activeStep;
        const current = idx === activeStep;
        const rejected = isRejected && idx === 1;
        const future = idx > activeStep;
        const circleColor = rejected ? '#ef4444' : done || current ? '#6366f1' : 'transparent';
        const borderColor = future ? inactiveBorder : rejected ? '#ef4444' : '#6366f1';
        const textColor = future ? muted : rejected ? '#ef4444' : activeText;

        return (
          <React.Fragment key={step.label}>
            {idx > 0 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mt: '13px',
                  bgcolor:
                    idx <= activeStep && !isRejected
                      ? '#6366f1'
                      : isRejected && idx <= 1
                        ? '#ef4444'
                        : inactiveTrack,
                }}
              />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: circleColor,
                  border: `2px solid ${borderColor}`,
                  color: done || current ? '#fff' : borderColor,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  boxShadow: current ? `0 0 0 4px ${rejected ? ringBad : ringOk}` : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {done && !isRejected ? (
                  <CheckCircleIcon sx={{ fontSize: '0.9rem', color: '#fff' }} />
                ) : rejected ? (
                  <CancelIcon sx={{ fontSize: '0.9rem', color: '#fff' }} />
                ) : (
                  idx + 1
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  mt: 0.75,
                  color: textColor,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {rejected ? 'Rejected' : step.label}
              </Typography>
              <Typography sx={{ fontSize: '0.62rem', color: muted, textAlign: 'center', lineHeight: 1.3 }}>
                {rejected ? 'Config rejected' : step.sub}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

type SSOPolicyBadgeProps = {
  riskLevel: string;
  /** @deprecated Colors are derived from theme mode; kept for call-site compatibility. */
  riskColors?: Record<string, string>;
  riskBg?: Record<string, string>;
};

export const SSOPolicyBadge: React.FC<SSOPolicyBadgeProps> = ({ riskLevel }) => {
  const { resolvedMode } = useTheme();
  const surface = getRiskSurface(riskLevel, resolvedMode);
  const [policy, setPolicy] = React.useState<{ policy_name: string; policy_type: string } | null>(null);
  const [code, setCode] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    fetch(
      `${API_ROOT}/okta/policy-mappings/resolve?risk_level=${encodeURIComponent(riskLevel)}`,
      authFetchInit(),
    )
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (!r.ok || data?.code === 'POLICY_LOOKUP_ERROR') {
          setCode('POLICY_LOOKUP_ERROR');
          setMessage(data?.message || 'Could not look up the security policy.');
          setPolicy(null);
          return;
        }
        setCode(data?.code || null);
        setMessage(data?.message || null);
        if (data?.code === 'POLICY_FOUND' && data?.policy) {
          setPolicy({
            policy_name: data.policy.policy_name,
            policy_type: data.policy.policy_type || '',
          });
        } else {
          setPolicy(null);
        }
      })
      .catch(() => {
        setCode('POLICY_LOOKUP_ERROR');
        setMessage('Could not look up the security policy.');
        setPolicy(null);
      })
      .finally(() => setChecked(true));
  }, [riskLevel]);

  if (!checked) return null;
  if (policy) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          border: `1px solid ${surface.border}`,
          backgroundColor: surface.bg,
        }}
      >
        <Chip
          label={riskLevel}
          size="small"
          sx={{ fontSize: '0.65rem', fontWeight: 700, height: 18, ...chipSxFromSurface(surface) }}
        />
        <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: surface.fg }}>{policy.policy_name}</Typography>
        {policy.policy_type && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>({policy.policy_type.replace(/_/g, ' ')})</Typography>
        )}
      </Box>
    );
  }
  if (code === 'POLICY_LOOKUP_ERROR') {
    return (
      <Alert severity="error" sx={{ py: 0.5, fontSize: '0.78rem' }}>
        {message || 'Could not look up the security policy.'}
      </Alert>
    );
  }
  if (code === 'POLICY_INACTIVE') {
    return (
      <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.78rem' }}>
        An active policy is not configured for <strong>{riskLevel}-risk</strong> apps.
      </Alert>
    );
  }
  if (code === 'RISK_LEVEL_MISSING' || code === 'RISK_LEVEL_INVALID') {
    return (
      <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.78rem' }}>
        {message || 'Application risk classification is missing or invalid.'}
      </Alert>
    );
  }
  if (code === 'POLICY_NOT_FOUND') {
    return (
      <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.78rem' }}>
        No policy configured for <strong>{riskLevel}-risk</strong> apps.
      </Alert>
    );
  }
  return null;
};

